/**
 * RetentionPreviewDemoPanel — C027 데이터 보존 정책 데모 (queue #646 §R646-2 항목4)
 * 강제 dryRun — matched 건수만 표시, deleted 는 항상 0. "실제 삭제 아님 — 미리보기" 배너.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRetentionPreview } from '../../hooks/useRetentionDemo';
import {
  cardStyle, sectionTitleStyle, subtitleStyle, labelStyle, inputStyle, monoStyle,
  primaryButtonStyle, resultBoxStyle, DemoSafetyBanner, DemoErrorState, DemoEmptyHint,
} from './demoShared';

export function RetentionPreviewDemoPanel() {
  const { t } = useTranslation();
  const previewMutation = useRetentionPreview();
  const [olderThanDays, setOlderThanDays] = useState(365);

  return (
    <section aria-labelledby="retention-panel-title">
      <h2 id="retention-panel-title" style={sectionTitleStyle}>{t('showcase.demo.retention.title')}</h2>
      <p style={subtitleStyle}>{t('showcase.demo.retention.subtitle')}</p>

      <DemoSafetyBanner text={t('showcase.demo.retention.banner')} />

      <div style={cardStyle}>
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label htmlFor="retention-days" style={labelStyle}>{t('showcase.demo.retention.olderThanDaysLabel')}</label>
          <input
            id="retention-days"
            type="number"
            min={1}
            value={olderThanDays}
            onChange={(e) => setOlderThanDays(Number(e.target.value))}
            style={inputStyle}
          />
        </div>

        <button
          type="button"
          onClick={() => previewMutation.mutate({ olderThanDays })}
          disabled={previewMutation.isPending || olderThanDays < 1}
          aria-busy={previewMutation.isPending}
          style={{ ...primaryButtonStyle, opacity: previewMutation.isPending ? 0.6 : 1 }}
        >
          {previewMutation.isPending ? t('showcase.demo.retention.previewing') : t('showcase.demo.retention.previewButton')}
        </button>

        <div style={{ marginTop: 'var(--spacing-4)' }}>
          {previewMutation.isError && (
            <DemoErrorState title={t('showcase.demo.common.callFailed')} error={previewMutation.error} />
          )}
          {!previewMutation.isError && !previewMutation.data && <DemoEmptyHint text={t('showcase.demo.retention.empty')} />}
          {previewMutation.data && !previewMutation.isError && (
            <div role="region" aria-label={t('showcase.demo.retention.resultLabel')} style={resultBoxStyle}>
              <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-2) var(--spacing-4)', margin: 0, fontSize: 'var(--font-size-sm)' }}>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.retention.tableLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle }}>{previewMutation.data.table}</dd>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.retention.matchedLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle }}>{previewMutation.data.matched}</dd>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.retention.deletedLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle }}>{previewMutation.data.deleted}</dd>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.retention.dryRunLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle, color: 'var(--color-status-success)', fontWeight: 700 }}>
                  {previewMutation.data.dryRun ? t('showcase.demo.common.yes') : t('showcase.demo.common.no')}
                </dd>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.retention.statusLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle, fontWeight: 700, color: previewMutation.data.status === 'ABORTED' ? 'var(--color-warning)' : 'var(--color-text-primary)' }}>
                  {previewMutation.data.status}
                </dd>
              </dl>
              {/* ★NON_NULL 생략 필드는 undefined — `!= null` 로 체크(!== null 금지) */}
              {previewMutation.data.message != null && (
                <p style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                  {previewMutation.data.message}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
